// articles/GettingStarted.js
// "Getting Started with Tee Time Organizer" (Sept 27, 2026). Replaces the in-app
// Implementation Guide modal (ImplementationGuideModal.js, last updated Feb 9, 2026),
// which had gone stale. First card in the 📖 Guides chooser, so a new organizer
// knows where to begin. Setup is written as ordered steps with the same step list,
// print layout and Close bar as the event guides; the reference material follows.
// Scoring and games belong to the "Running your weekly rounds" guide; events to the
// event guides — not repeated here. No prices (Dennis, Sept 27): plans are named
// with what they unlock, and the pricing page carries the numbers.
// Button and tab names are quoted from the live screens; if a label changes in
// App.js, change it here too.

import React from 'react';
import { colors, getGuideBySlug } from '../guidesData';
import { H2, H3, P, UL, LI, InlineLink, GuideCTA, FAQ } from '../articleUI';
import { Step, Btn, Heads, StepTracker, PRINT_CSS } from './eventGuideParts';

const guide = getGuideBySlug('getting-started-with-tee-time-organizer');

const FAQS = [
  {
    q: 'Do players need to create an account or download an app?',
    a: 'No. Each player gets a personal link to his player app in his first email from TTO. No password, no download. He can add it to his phone’s home screen so it opens like an app.',
  },
  {
    q: 'What if a player doesn’t receive emails?',
    a: 'Check his email address in Admin → Players, then ask him to look in his spam or junk folder and add noreply@teetimeorganizer.net to his contacts.',
  },
  {
    q: 'How do I add a guest or one-time player?',
    a: 'Add him in Admin → Players with the Guest role, or send him the tee time’s 🎟️ Invite link: when he opens it and gives his name and email, he is added as a guest and registered for that tee time. Guests don’t get new tee time notices; change his role to Player if you want him to.',
  },
  {
    q: 'Do players move off the waitlist automatically?',
    a: 'Yes. When a tee time is full, new RSVPs go on the waitlist. When someone cancels, the first waitlisted player is moved up and notified.',
  },
  {
    q: 'How do I cancel a tee time that has players on it?',
    a: 'Use ⋯ → Delete tee time on its card. When players are registered, TTO asks for an optional reason, which is included in the cancellation notice sent to each of them.',
  },
  {
    q: 'Can there be more than one organizer?',
    a: 'Yes. In Settings → Additional Organizers, invite a person who is already in your player list. Organizers can do everything you can except remove the owner.',
  },
  {
    q: 'Can a player belong to more than one TTO group?',
    a: 'Yes. In his player app he can link other TTO organizations he belongs to and see all his tee times in one place.',
  },
  {
    q: 'Why won’t Balanced Flights run?',
    a: 'Balanced Flights ranks players by course handicap, so every player needs a Handicap Index (and the course needs its tee ratings). Add the missing handicaps, or use Randomize instead.',
  },
  {
    q: 'How often is the weather updated?',
    a: 'Every two hours, for tee times within the forecast window.',
  },
  {
    q: 'My scorecard doesn’t show a course rating or slope. What do I do?',
    a: 'Import it anyway. 📷 Import from Scorecard reads the holes and holds each tee in the Scorecard import panel, with boxes for its course rating and slope. Get the numbers from the pro shop or the course lookup on ghin.com, type them in and save. TTO doesn’t save a tee without both, because course handicaps can’t be worked out without them.',
  },
];

export default function GettingStarted() {
  return (
    <article>
      <h1
        style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 38,
          fontWeight: 400,
          color: colors.gray900,
          margin: '0 0 8px',
          lineHeight: 1.15,
        }}
      >
        Getting Started with Tee Time Organizer
      </h1>
      <style>{PRINT_CSS}</style>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 28px' }}
      >
        <p style={{ fontSize: 15, color: colors.gray400, margin: 0 }}>
          {guide.readMinutes} min read &middot; Start here
        </p>
        <button
          type="button"
          className="guide-no-print"
          onClick={() => window.print()}
          style={{
            background: 'white',
            color: colors.green800,
            border: `1px solid ${colors.gray300}`,
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          🖨️ Print this guide
        </button>
      </div>

      <P>
        Welcome to Tee Time Organizer. This guide takes a new organizer from an empty account to a
        first published tee time, in the order the steps need to happen: your settings, courses,
        players, the tee time itself, invitations, reminders and pairings. Most groups are set up in
        under an hour. The second half of the guide is reference material to come back to.
      </P>

      <StepTracker
        storageKey="tto_guide_getting_started_steps"
        steps={[
          'Organization settings',
          'Courses and tees',
          'Players',
          'Player groups',
          'Your first tee time',
          'Publish and invite',
          'Welcome email, reminders, messages',
          'Pairings and the tee sheet',
        ]}
        note="Steps 1 to 4 are done once. Steps 5 to 8 are what you repeat for each round. Tap a step to jump to it."
      />

      <H2>Set up, step by step</H2>

      <Step n={1} title="Check your organization settings" when="Once, when you first sign in">
        <P>
          Open <Btn>🏢 Settings</Btn>. Each section opens with a click; these are the ones to check
          first.
        </P>
        <UL>
          <LI>
            <strong>Identity &amp; Branding:</strong> your organization&rsquo;s name and the name your
            emails come from.
          </LI>
          <LI>
            <strong>Organizer Contact &amp; Email Settings:</strong> where player replies go. You can
            also have the tee sheet emailed to the pro shop automatically.
          </LI>
          <LI>
            <strong>Location &amp; Time Zone</strong> and <strong>Display Preferences</strong>: so
            times, dates and weather are right for your area.
          </LI>
          <LI>
            <strong>Player Reminders:</strong> the times for the automatic 24-hour and same-day
            reminders, an optional custom reminder (a set number of days before), and whether
            reminders include the weather.
          </LI>
          <LI>
            <strong>Player Registration:</strong> whether players may reserve spots for other
            players, and whether that is open to anyone or only players with the Captain role.
          </LI>
          <LI>
            <strong>Tee Sheet Display Options:</strong> what shows on tee sheets, such as handicaps
            and badges.
          </LI>
          <LI>
            <strong>Additional Organizers:</strong> invite a co-organizer who can run things when
            you&rsquo;re away. He must already be in your player list (step 3), so you may want to
            come back to this.
          </LI>
        </UL>
      </Step>

      <Step n={2} title="Add your courses and tees" when="Once for each course you play">
        <UL>
          <LI>
            Go to <Btn>⚙️ Admin</Btn> &rarr; <strong>Courses</strong> and click{' '}
            <Btn>+ Add Course</Btn>.
          </LI>
          <LI>
            <strong>Search first:</strong> type the course name to search more than 25,000 courses
            and import it. Otherwise enter the course name, its location (for the weather), 9 or 18
            holes, and optionally the pro shop phone and email (used to send the tee sheet) and the
            interval between tee times.
          </LI>
          <LI>
            <strong>Tees from a scorecard photo:</strong> save the course, then open it again with
            Edit. Under <strong>Course Tees</strong>, click <Btn>📷 Import from Scorecard</Btn> and
            take or choose a photo of the scorecard. In 10 to 15 seconds it reads every tee on the
            card: name, course rating, slope rating and par, plus each hole&rsquo;s par, yardage and
            hole handicap (HCP).
          </LI>
          <LI>
            <strong>No rating or slope on the card?</strong> Many club scorecards print only
            yardage, par and handicap. TTO holds those tees in the <strong>Scorecard import</strong>{' '}
            panel with their holes already read. Type in each tee&rsquo;s course rating and slope
            (ask the pro shop, or use the course lookup on ghin.com) and save. A tee without both
            numbers isn&rsquo;t saved: course handicaps can&rsquo;t be worked out without them.
          </LI>
          <LI>
            <strong>Photo tips:</strong> lay the card flat in good light with the whole card in the
            frame. If the import asks for a JPG or PNG, take a screenshot of the photo and import
            that. For a two-sided card, import each side.
          </LI>
          <LI>
            <strong>By hand:</strong> click <Btn>+ Add Tee</Btn> for each set of tees (name, color,
            course rating, slope rating and par), then fill in its <strong>Hole Data</strong>: par,
            yardage and hole handicap for every hole.
          </LI>
          <LI>
            <strong>Check it:</strong> after any import, open each tee&rsquo;s hole data and compare
            it with the card. A misread hole handicap changes who gets strokes where.
          </LI>
        </UL>
        <Heads>
          <strong>Why tees matter.</strong> The rating, slope and par turn each player&rsquo;s
          Handicap Index into a course handicap for the tees he plays. Scoring and games also need
          the hole-by-hole pars and hole handicaps. Without them, handicaps and scoring can&rsquo;t be
          worked out.
        </Heads>
      </Step>

      <Step n={3} title="Add your players" when="Once, then as your group grows">
        <UL>
          <LI>
            Go to <Btn>⚙️ Admin</Btn> &rarr; <strong>Players</strong> and click{' '}
            <Btn>+ Add Player</Btn>. A name and an email address are all you need to start.
          </LI>
          <LI>
            <strong>Have your group in a spreadsheet?</strong> <Btn>📥 Import players</Btn> brings
            the whole list in at once from a CSV or Excel file, or rows pasted from it. Choose{' '}
            <strong>Regular players</strong> for your group&rsquo;s regulars, or add a{' '}
            <strong>Role</strong> column (Player or Guest) to mix regulars and guests in one list.
            A list with more new regulars than your plan allows is refused, with the number to
            upgrade for or change to Guest.
          </LI>
          <LI>
            <strong>Also worth filling in:</strong> phone (for text reminders), GHIN number,{' '}
            <strong>Handicap Index</strong> and <strong>preferred tee</strong>, so his course handicap
            can be determined. Players can update their own profile later in the player app.
          </LI>
          <LI>
            <strong>Role:</strong> Player for regulars; Guest for one-time players (guests aren&rsquo;t
            sent new tee time notices); Captain for players who may reserve spots for others;
            Organizer for co-organizers.
          </LI>
          <LI>
            <strong>Updating handicaps in bulk:</strong> <Btn>📄 Upload Handicap Report</Btn> reads a
            PDF, Excel or CSV report and updates everyone&rsquo;s Handicap Index at once.
          </LI>
          <LI>
            <Btn>📥 Export CSV</Btn> downloads your player list with contact details.
          </LI>
          <LI>
            When someone leaves, set his status to Inactive rather than deleting him. His history is
            kept and he drops off your active lists.
          </LI>
        </UL>
      </Step>

      <Step n={4} title="Create player groups (optional)" when="Once, if your players fall into regular groups">
        <UL>
          <LI>
            Go to <Btn>⚙️ Admin</Btn> &rarr; <strong>Groups</strong> and click{' '}
            <Btn>+ Create Group</Btn>, for example &ldquo;Tuesday Morning Regulars&rdquo; or
            &ldquo;Weekend Group&rdquo;.
          </LI>
          <LI>
            Groups work like distribution lists. Use them to add several players to a tee time at
            once, to send a new tee time notice to one group only, and to message a group.
          </LI>
          <LI>A player can be in any number of groups.</LI>
        </UL>
      </Step>

      <Step n={5} title="Create your first tee time" when="For each round">
        <UL>
          <LI>
            On the <Btn>🗓 Tee Times</Btn> tab, click <Btn>+ New tee time</Btn>.
          </LI>
          <LI>
            <strong>Choose how many to create:</strong>
            <UL>
              <LI>
                <Btn>Simple</Btn>: one tee time. The most common choice.
              </LI>
              <LI>
                <Btn>Recurring</Btn>: a series, for example every Tuesday at 7:30 AM for 12 weeks.
              </LI>
              <LI>
                <Btn>Bulk</Btn>: several tee times on the same day, from a start and end time, so
                players can choose their time.
              </LI>
            </UL>
          </LI>
          <LI>
            Fill in the <strong>course</strong>, <strong>date</strong>, <strong>time</strong> and{' '}
            <strong>slots</strong> (the number of players).
          </LI>
          <LI>
            <strong>Options:</strong> <Btn>Private Tee Time</Btn> (only players you add or invite see
            it); <Btn>18H</Btn> / <Btn>F9</Btn> / <Btn>B9</Btn> for the holes played;{' '}
            <strong>Start Type</strong> Tee Times or Shotgun; and an optional <strong>Title</strong>{' '}
            shown to players.
          </LI>
          <LI>
            Click <Btn>Add Tee Time</Btn>. It starts as a draft, marked{' '}
            <strong>📝 Not Published</strong>, and only you can see it.
          </LI>
        </UL>
        <Heads>
          <strong>Tip for groups:</strong> rather than one tee time per foursome, many organizers
          create a single tee time with all the slots (say 12). Players RSVP to it, and you split them
          into groups on the Pairings page (step 8).
        </Heads>
      </Step>

      <Step n={6} title="Publish it and invite players" when="When you're ready for RSVPs">
        <UL>
          <LI>
            Click <Btn>📣 Publish</Btn> on the tee time card and choose who is notified:{' '}
            <strong>All Active Players</strong>, <strong>Select Specific Players</strong>, a{' '}
            <strong>Player Group</strong>, or <Btn>Send No Notifications</Btn>. You can add a short
            message.
          </LI>
          <LI>
            <strong>Once published, the tee time appears in every active player&rsquo;s app</strong>,
            where he can RSVP, whether or not he was notified. A private tee time appears only to the
            players you add or invite, and can&rsquo;t be published to all players.
          </LI>
          <LI>
            <Btn>🎟️ Invite</Btn> gives you a link to the tee time to send yourself, by email or text.
            Whoever opens it enters his name and email and is registered.
          </LI>
          <LI>
            <strong>Manage Players</strong> on the card adds or removes players yourself, one at a time
            or a whole group. When the tee time is full, extra players go on the waitlist and move up
            automatically when someone cancels.
          </LI>
          <LI>
            Changed something after publishing? <Btn>📧 Notify Players</Btn> sends an update.
          </LI>
        </UL>
      </Step>

      <Step n={7} title="Welcome players, and set up reminders and messages" when="Once, then as needed">
        <H3>The welcome email</H3>
        <UL>
          <LI>
            Go to <Btn>⚙️ Admin</Btn> &rarr; <strong>Message</strong> &rarr;{' '}
            <Btn>👋 Welcome Email</Btn>. Each player receives his personal link to the player app.
          </LI>
          <LI>
            Sending it after your first tee time is published means players find something to RSVP
            to when they open the link.
          </LI>
        </UL>
        <H3>Automatic reminders</H3>
        <UL>
          <LI>
            Confirmed players get the 24-hour and same-day reminders at the times set in{' '}
            <strong>Settings &rarr; Player Reminders</strong> (step 1), plus the custom reminder if
            you turned it on. Reminders include the weather, the course and a link to the player app.
          </LI>
          <LI>
            <strong>GHIN posting reminder:</strong> a set number of hours after the round, players are
            reminded to post their score. Players who have connected GHIN and already posted are
            skipped. It is not sent for rounds you mark as not GHIN eligible. Set the timing in the
            GHIN Posting Reminder template (below).
          </LI>
          <LI>
            <strong>Text messages:</strong> players who opt in on their profile get reminders by text
            as well as, or instead of, email. Texting is included from the Plus plan up.
          </LI>
        </UL>
        <H3>Message templates</H3>
        <UL>
          <LI>
            <Btn>⚙️ Admin</Btn> &rarr; <strong>Message</strong> &rarr; <Btn>📝 Templates</Btn> holds
            the wording of every automatic email: New Tee Time, 24Hr Reminder, Same Day Reminder,
            Custom Reminder, Waitlist Alert, Cancellation Notice, Welcome Email and GHIN Posting
            Reminder.
          </LI>
          <LI>
            <strong>Placeholders</strong> fill in details when a message is sent. Any template can use
            the player&rsquo;s name, the course, date, time, weather and his player app link.
            Reminders can also include the tee sheet (with or without course handicaps), the live
            leaderboard link and his target score. The template screen lists what each one allows.
          </LI>
        </UL>
        <H3>Messages you send yourself</H3>
        <UL>
          <LI>
            <Btn>⚙️ Admin</Btn> &rarr; <strong>Message</strong> &rarr; <Btn>✉️ Player Message</Btn>{' '}
            sends a note to all players, a group or chosen players: a frost delay, a rain-out, a
            change of plan.
          </LI>
          <LI>
            Everything sent is listed in <Btn>🛠️ Tools</Btn> &rarr; <strong>📧 Message Log</strong>.
          </LI>
        </UL>
      </Step>

      <Step n={8} title="Set the pairings and send the tee sheet" when="Once the RSVPs are in">
        <UL>
          <LI>
            Open the <Btn>⛳ Pairings</Btn> tab and choose the tee time.
          </LI>
          <LI>
            <strong>Arrange the groups:</strong>
            <UL>
              <LI>
                <Btn>🎲 Randomize</Btn>: shuffles everyone into groups.
              </LI>
              <LI>
                <Btn>⛳ HCP Sort</Btn>: orders the field by course handicap.
              </LI>
              <LI>
                <Btn>📋 As Booked</Btn>: keeps the order players signed up.
              </LI>
              <LI>
                <Btn>⚖️ Balanced Flights ▾</Btn>: ranks players A, B, C and D by course handicap and
                puts one of each in every group, so the groups are evenly matched. Also for
                threesomes and twosomes. Every player needs a Handicap Index.
              </LI>
              <LI>Move any player by hand afterwards.</LI>
            </UL>
          </LI>
          <LI>
            <strong>Shotgun start?</strong> <Btn>⚡ Shotgun Setup</Btn> assigns starting holes.
          </LI>
          <LI>
            Click <Btn>💾 Save Pairings</Btn> to save the groups and show them in each player&rsquo;s
            app, or <Btn>Save without sharing</Btn> to keep them private until you&rsquo;re ready.
          </LI>
          <LI>
            <Btn>📧 Email</Btn> sends the tee sheet to the pro shop, to the players on it, or to chosen
            players. <Btn>🖨️ Tee sheet</Btn> prints it, and <Btn>⬇ Export ▾</Btn> downloads the
            pairings (including a file for Golf Genius).
          </LI>
          <LI>
            The <Btn>🖨️ Print</Btn> tab gathers everything printable for a round in one place.
          </LI>
        </UL>
      </Step>

      <H2>What&rsquo;s next</H2>
      <UL>
        <LI>
          <strong>Scoring:</strong> players scoring on their phones, the leaderboard, posting to GHIN,
          scorecards and stats are covered in{' '}
          <InlineLink to="/guides/scoring-your-golf-rounds">Scoring Your Rounds</InlineLink>.
        </LI>
        <LI>
          <strong>Tournaments and events:</strong> see the event setup guides, one per format, for
          example{' '}
          <InlineLink to="/guides/how-to-set-up-a-captains-choice-golf-tournament">
            Captain&rsquo;s Choice
          </InlineLink>{' '}
          and{' '}
          <InlineLink to="/guides/how-to-set-up-a-best-ball-golf-tournament">Best Ball</InlineLink>.
        </LI>
      </UL>

      <H2>Reference</H2>

      <H3>The player app</H3>
      <UL>
        <LI>
          Every player has a personal link, sent in his first email from TTO. No password, no
          download.
        </LI>
        <LI>
          In it he can RSVP, cancel or join a waitlist, see his upcoming rounds, update his profile
          (phone, Handicap Index, preferred tee, photo), add rounds to his calendar and see his stats
          (<strong>📊 Stats</strong>).
        </LI>
        <LI>
          <strong>Add to Home Screen:</strong> opened in Safari (iPhone) or Chrome (Android), the
          link can be added to the phone&rsquo;s home screen and then opens like an app.
        </LI>
        <LI>
          <strong>Linked Organizations:</strong> a player in more than one TTO group can link them
          and see all his tee times in one place.
        </LI>
        <LI>
          <strong>Notifications:</strong> email is on by default. Text messages are opt-in, and he
          must tick the consent box himself. He can turn off the GHIN posting reminder.
        </LI>
      </UL>

      <H3>Reports</H3>
      <UL>
        <LI>
          <Btn>⚙️ Admin</Btn> &rarr; <strong>Reports</strong>: a summary, player activity, course
          usage and monthly trends.
        </LI>
        <LI>
          <Btn>🗓 Tee Times</Btn> has card and list views, filters (Upcoming, Today, Past, All), a
          search by course or player, <Btn>📥 Export</Btn> and <Btn>🖨️ Print list</Btn>.
        </LI>
      </UL>

      <H3>Plans</H3>
      <UL>
        <LI>
          <strong>Free</strong>, for groups of up to 8 players, is free for as long as you use it. It
          includes tee times, RSVPs, the waitlist, email reminders, weather, the player app, course
          handicaps, groups, message templates and additional organizers.
        </LI>
        <LI>
          <strong>Starter</strong> (up to 20 players) adds Balanced Flights, shotgun setup, scoring
          and posting scores to GHIN.
        </LI>
        <LI>
          <strong>Plus</strong> (up to 40 players) adds text message reminders and automatic handicap
          updates from GHIN.
        </LI>
        <LI>
          <strong>Professional</strong> (up to 100) and <strong>League</strong> (up to 250) include
          everything in Plus for larger groups.
        </LI>
        <LI>
          A 🔒 badge in the app marks a feature your plan doesn&rsquo;t include. See your plan and
          player count in <strong>Settings &rarr; Subscription &amp; Billing</strong>, and current
          prices on our <InlineLink to="/">home page</InlineLink>.
        </LI>
      </UL>

      <H3>Referral program</H3>
      <UL>
        <LI>
          Know another organizer? Share TTO from <strong>Settings &rarr; Referral Program</strong>{' '}
          (or your players can, from the <strong>📤 Share</strong> button in their app).
        </LI>
        <LI>
          You earn a free month once the organization you referred has been active for six months.
          Free months are applied to your subscription automatically.
        </LI>
      </UL>

      <H3>Getting help</H3>
      <UL>
        <LI>
          <Btn>ⓘ Help</Btn> on each page explains that page. <Btn>📖 Guides</Btn> opens this guide and
          the others. <Btn>What&rsquo;s New</Btn> lists recent changes.
        </LI>
        <LI>Email us at support@teetimeorganizer.net.</LI>
      </UL>

      <H3>Good habits</H3>
      <UL>
        <LI>
          <strong>Post tee times early</strong> and ask players to cancel at least 48 hours ahead, so
          the waitlist works and you can release unneeded tee times to the club in time.
        </LI>
        <LI>
          <strong>Watch your numbers.</strong> A growing waitlist may mean asking for another tee
          time; more tee times than players may mean giving one back.
        </LI>
        <LI>
          <strong>Use recurring tee times</strong> for a regular game.
        </LI>
        <LI>
          <strong>Keep player details current:</strong> email addresses, handicaps and preferred
          tees.
        </LI>
        <LI>
          <strong>Have a backup organizer</strong> who can step in when you&rsquo;re away.
        </LI>
        <LI>
          <strong>Help new players get set up:</strong> bookmark the player app link, add it to the
          home screen, add noreply@teetimeorganizer.net to contacts, opt in to texts if wanted, and
          add a photo.
        </LI>
      </UL>

      <FAQ id="getting-started" faqs={FAQS} />
      <div className="guide-no-print">
        <GuideCTA />
      </div>
    </article>
  );
}
